import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { 
  FiUser, FiGlobe, FiChevronDown, FiPhone, FiFacebook, 
  FiInstagram, FiLinkedin, FiTwitter
} from 'react-icons/fi';
import { siteContentApi } from '../api/phpApi';

export default function Navbar() {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';
  const [langOpen, setLangOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navSettings, setNavSettings] = useState({
    top_phone: '+212 5 28 00 00 00',
    top_email: 'contact@iamar.ma',
    linkedin_url: 'https://linkedin.com',
    facebook_url: 'https://facebook.com',
    instagram_url: 'https://instagram.com',
    twitter_url: 'https://twitter.com'
  });

  useEffect(() => {
    const fetchNav = async () => {
      try {
        const data = await siteContentApi.getByKey('header_nav_settings');
        if (data && data.content) {
          const parsed = JSON.parse(data.content);
          setNavSettings(prev => ({ ...prev, ...parsed }));
        }
      } catch (e) {
        // Fallback to default
      }
    };
    fetchNav();
  }, []);
  
  const getLangFromURL = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('lang') || 'fr';
    } catch {
      return 'fr';
    }
  };
  
  const [lang, setLang] = useState(getLangFromURL());
  const languages = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'العربية' }
  ];

  const langDropdownRef = useRef(null);
  const menuRef = useRef(null);
  
  useEffect(() => {
    const onClick = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const changeLang = (code) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', code);
      document.documentElement.lang = code;
      window.location.href = url.toString();
    } catch {
      setLang(code);
      setLangOpen(false);
    }
  };

  const handleDropdownToggle = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  // Helper to scroll to ID if on about page
  const handleScrollLink = (id) => {
    setActiveDropdown(null);
    closeSidebar();
    
    if (window.location.pathname === '/about') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // update hash without scroll jump
        window.history.pushState(null, null, `#${id}`);
      }
    } else {
      window.location.href = `/about#${id}`;
    }
  };

  useEffect(() => {
    const links = [
      'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;600;700;800&display=swap',
      'https://fonts.googleapis.com/css2?family=Noto+Sans+Tifinagh&display=swap'
    ];

    const added = [];
    links.forEach(href => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = href;
        document.head.appendChild(l);
        added.push(l);
      }
    });

    return () => {
      added.forEach(l => l.parentNode && l.parentNode.removeChild(l));
    };
  }, []);

  return (
    <>
      <nav
        className="navbar"
        style={{ fontFamily: "'Montserrat', 'Noto Kufi Arabic', 'Noto Sans Tifinagh', sans-serif" }}
      >
        <div className="navbar-info-bar">
          <div className="info-bar-left">
            <div className="info-item">
              <FiPhone size={14} />
              <span>{navSettings.top_phone || 'Téléphone: +212 5 28 00 00 00'}</span>
            </div>
            <span className="info-divider">|</span>
            <div className="info-item social-icons">
              <span>Suivez-nous sur:</span>
              <a href={navSettings.facebook_url || 'https://facebook.com'} target="_blank" rel="noopener noreferrer"><FiFacebook size={14} /></a>
              <a href={navSettings.instagram_url || 'https://instagram.com'} target="_blank" rel="noopener noreferrer"><FiInstagram size={14} /></a>
              <a href={navSettings.linkedin_url || 'https://linkedin.com'} target="_blank" rel="noopener noreferrer"><FiLinkedin size={14} /></a>
              <a href={navSettings.twitter_url || 'https://twitter.com'} target="_blank" rel="noopener noreferrer"><FiTwitter size={14} /></a>
            </div>
          </div>

          <div className="info-bar-right">
            <div className="nav-actions">
              <div ref={langDropdownRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="nav-lang-btn"
                  onClick={() => setLangOpen((s) => !s)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 , backgroundColor: 'transparent', border: 'none', cursor: 'pointer' , color: 'white', fontFamily: lang === 'ar' ? "Noto Kufi Arabic" : 'inherit', direction: lang === 'ar' ? 'rtl' : 'ltr', fontSize: '0.8rem' }}
                >
                  <FiGlobe size={16} />
                  {(() => {
                    const sel = languages.find(l => l.code === lang) || { code: lang, label: lang };
                    return <span>{sel.label}</span>;
                  })()}
                  <FiChevronDown size={12} />
                </button>

                {langOpen && (
                  <ul className="nav-lang-dropdown">
                    {languages.map((l) => (
                      <li key={l.code}>
                        <button onClick={() => changeLang(l.code)} style={{ display: 'block', width: '100%', padding: '6px 10px', background: l.code === lang ? 'rgba(0,0,0,0.05)' : 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', color: '#1b1e4f', fontFamily: "Montserrat", fontSize: '0.85rem' }}>
                          {l.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Link to="/admin" className="nav-admin" style={{ color: 'white' }}>
                <FiUser size={18} />
              </Link>
            </div>
          </div>
        </div>

        <div className="navbar-top">
          <div className="navbar-logo">
            <Link to="/">
              <img src={logo} alt="IAMAR logo" />
            </Link>
          </div>

          <button className={`nav-toggle ${sidebarOpen ? 'active' : ''}`} onClick={toggleSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {!isAdminPage && (
          <div className="navbar-bottom" ref={menuRef}>

          <ul className="nav-links">
            <li><Link to="/">Home</Link></li>
            
            <li className={`nav-dropdown-item ${activeDropdown === 'institute' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('institute')} className="nav-dropdown-btn">
                Institute <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'institute' ? 'is-visible' : ''}`}>
                <li><button onClick={() => handleScrollLink('presentation')} className="nav-sub-btn">About IAMAR</button></li>
                <li><button onClick={() => handleScrollLink('vision')} className="nav-sub-btn">Vision IAMAR</button></li>
                <li><button onClick={() => handleScrollLink('impact')} className="nav-sub-btn">International Impact</button></li>
                <li><button onClick={() => handleScrollLink('fondateur')} className="nav-sub-btn">Founder</button></li>
                <li><button onClick={() => handleScrollLink('partenariats')} className="nav-sub-btn">Partnerships</button></li>
                <li><button onClick={() => handleScrollLink('gouvernance')} className="nav-sub-btn">Governance</button></li>
              </ul>
            </li>

            <li className={`nav-dropdown-item ${activeDropdown === 'gsida' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('gsida')} className="nav-dropdown-btn">
                GSIDA <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'gsida' ? 'is-visible' : ''}`}>
                <li><Link to="/gsida">Congress Overview</Link></li>
                <li><Link to="/gsida/theme-2026">Theme 2026</Link></li>
                <li><Link to="/gsida/program">Program</Link></li>
                <li><Link to="/gsida/speakers">Speakers</Link></li>
                <li><Link to="/gsida/partners">Partners</Link></li>
                <li><Link to="/gsida/publications">Publications</Link></li>
                {/* <li><Link to="/gsida/gallery">Media Gallery</Link></li> */}
              </ul>
            </li>

            <li className={`nav-dropdown-item ${activeDropdown === 'trainings' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('trainings')} className="nav-dropdown-btn">
                Formations <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'trainings' ? 'is-visible' : ''}`}>
                <li><Link to="/trainings/executive-programs">Executive Programs GSIDA</Link></li>
                {/* <li><Link to="/trainings/certification">Certification Programs</Link></li>
                <li><Link to="/trainings/short-courses">Short Courses (2 days)</Link></li>
                <li><Link to="/trainings/public-sector">Public Sector Training</Link></li>
                <li><Link to="/trainings/security-defense">Security & Defense Training</Link></li>
                <li><Link to="/trainings/catalog">Download Catalog</Link></li> */}
              </ul>
            </li>

            {/* ... other items can be updated similarly if needed */}
             <li className={`nav-dropdown-item ${activeDropdown === 'challenge' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('challenge')} className="nav-dropdown-btn">
                GSIDA Academic Challenge <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'challenge' ? 'is-visible' : ''}`}>
                <li><Link to="/challenge/doctoral-competition">Doctoral Competition</Link></li>
                <li><Link to="/challenge/awards">GSIDA Awards</Link></li>
                <li><Link to="/challenge/hackathon">Hackathon</Link></li>
                <li><Link to="/challenge/public-innovation">Public Innovation</Link></li>
              </ul>
            </li>

            <li className={`nav-dropdown-item ${activeDropdown === 'apsis' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('apsis')} className="nav-dropdown-btn">
                APSIS 2026 <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'apsis' ? 'is-visible' : ''}`}>
                <li><Link to="/apsis">About APSIS</Link></li>
                <li><Link to="/apsis/exhibitors">Exhibitors</Link></li>
                <li><Link to="/apsis/security-innovation">Security Innovation</Link></li>
                <li><Link to="/apsis/startups">Startups & Tech</Link></li>
                <li><Link to="/apsis/industrial-partners">Industrial Partners</Link></li>
                <li><Link to="/apsis/registration">Registration</Link></li>
              </ul>
            </li>

            <li><Link to="/experts" className="nav-link-simple">Experts</Link></li>
            <li><Link to="/research" className="nav-link-simple">Comité scientifique</Link></li>

           <li className={`nav-dropdown-item ${activeDropdown === 'publications' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('publications')} className="nav-dropdown-btn">
                Publications <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'publications' ? 'is-visible' : ''}`}>
                <li><Link to="/publications">All Publications</Link></li>
                <li><Link to="/publications/journals">Acte de congrè GSIDA 2026</Link></li>
                <li><Link to="/publications/books">Rapport GSIDA 2025</Link></li>
       
              </ul>
            </li>


            <li className={`nav-dropdown-item ${activeDropdown === 'Revue RIGSIDA' ? 'is-open' : ''}`}>
              <button onClick={() => handleDropdownToggle('Revue RIGSIDA')} className="nav-dropdown-btn">
                Revue RIGSIDA <FiChevronDown />
              </button>
              <ul className={`nav-dropdown-content ${activeDropdown === 'Revue RIGSIDA' ? 'is-visible' : ''}`}>
                <li><Link to="/revue">À propos de la revue</Link></li>
                <li><Link to="/revue/numbers">Les numéros</Link></li>
             
              </ul>
            </li>

            
          </ul>
          </div>
        )}
      </nav>


      <div className={`nav-overlay ${sidebarOpen ? 'show' : ''}`} onClick={closeSidebar}></div>

      <div className={`nav-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
           <img src={logo} alt="Logo" style={{ height: '40px' }} />
           <Link to="/admin" onClick={closeSidebar} className="nav-admin" style={{ color: '#1b1e4f' }}>
              <FiUser size={22} />
           </Link>
        </div>
        <ul className="nav-sidebar-links">
          <li><Link to="/" onClick={closeSidebar}>Home</Link></li>
          <li className="sidebar-dropdown">
            <span className="sidebar-label">Institute</span>
            <ul>
              <li><button onClick={() => handleScrollLink('presentation')} className="sidebar-sub-btn">About IAMAR</button></li>
              <li><button onClick={() => handleScrollLink('vision')} className="sidebar-sub-btn">Vision IAMAR</button></li>
              <li><button onClick={() => handleScrollLink('fondateur')} className="sidebar-sub-btn">Founder</button></li>
              <li><button onClick={() => handleScrollLink('gouvernance')} className="sidebar-sub-btn">Governance</button></li>
            </ul>
          </li>
          <li><Link to="/gsida" onClick={closeSidebar}>GSIDA</Link></li>
          <li><Link to="/trainings" onClick={closeSidebar}>Formations</Link></li>
          <li><Link to="/apsis" onClick={closeSidebar}>APSIS 2026</Link></li>
            <li><Link to="/experts" onClick={closeSidebar}>Experts</Link></li>
            <li><Link to="/research" onClick={closeSidebar}>Comité scientifique</Link></li>
            <li><Link to="/publications" onClick={closeSidebar}>Publications</Link></li>
              <li><Link to="/revue" onClick={closeSidebar}>Revue RIGSIDA</Link></li>
         
          
        </ul>
      </div>
    </>
  );
}
